/**
 * Document Upload Components for Quote-Native PDF Ingestion
 * 
 * NON-NEGOTIABLE RULES:
 * - Document is source of truth - AI may extract, never invent
 * - Human verification required before data becomes active
 * - Zero guessing - unclear values require user input
 * - Permanent traceability - every item links to source document
 */
import React, { useState, useRef, useCallback } from 'react';
import { 
  Upload, FileText, Check, X, AlertTriangle, Info, 
  ChevronLeft, ChevronRight, Eye, Edit2, Save, RefreshCw,
  FileWarning, CheckCircle, Clock, Sparkles, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ CONFIDENCE BADGE ============

export function ConfidenceBadge({ confidence, showLabel = true }) {
  const configs = {
    high: { color: 'var(--status-success)', bg: 'rgba(34, 197, 94, 0.15)', label: 'High' },
    medium: { color: 'var(--status-warning)', bg: 'rgba(234, 179, 8, 0.15)', label: 'Medium' },
    low: { color: 'var(--status-error)', bg: 'rgba(239, 68, 68, 0.15)', label: 'Low' },
    not_found: { color: 'var(--text-muted)', bg: 'rgba(107, 114, 128, 0.15)', label: 'Not Found' }
  };

  const config = configs[confidence] || configs.not_found;

  return (
    <span
      data-testid={`confidence-badge-${confidence}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 6px',
        borderRadius: '4px',
        background: config.bg,
        color: config.color,
        fontSize: '0.625rem',
        fontWeight: 500,
        textTransform: 'uppercase'
      }}
    >
      {confidence === 'high' && <CheckCircle size={10} />}
      {confidence === 'medium' && <AlertTriangle size={10} />}
      {confidence === 'low' && <FileWarning size={10} />}
      {showLabel && config.label}
    </span>
  );
}

// ============ STATUS BADGE ============

export function StatusBadge({ status }) {
  const configs = {
    PENDING_REVIEW: { color: 'var(--status-warning)', label: 'Pending Review' },
    VERIFIED: { color: 'var(--status-success)', label: 'Verified' },
    ARCHIVED: { color: 'var(--text-muted)', label: 'Archived' },
    SUPERSEDED: { color: 'var(--text-muted)', label: 'Superseded' }
  };

  const config = configs[status] || configs.PENDING_REVIEW;

  return (
    <span
      data-testid={`status-badge-${status}`}
      style={{
        padding: '4px 8px',
        borderRadius: 'var(--radius-sm)',
        background: `${config.color}20`,
        color: config.color,
        fontSize: '0.75rem',
        fontWeight: 500
      }}
    >
      {config.label}
    </span>
  );
}

// ============ UPLOAD DROPZONE ============

export function UploadDropzone({ onFileSelect, isUploading, acceptedTypes = '.pdf' }) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOut = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = [...e.dataTransfer.files];
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        onFileSelect(file);
      }
    }
  }, [onFileSelect]);

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      onFileSelect(file);
    }
  };

  return (
    <div
      data-testid="upload-dropzone"
      onClick={() => !isUploading && fileInputRef.current?.click()}
      onDragEnter={handleDragIn}
      onDragLeave={handleDragOut}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      style={{
        border: `2px dashed ${isDragging ? 'var(--accent-primary)' : 'var(--glass-border)'}`,
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-3xl)',
        textAlign: 'center',
        cursor: isUploading ? 'not-allowed' : 'pointer',
        transition: 'all var(--transition-base)',
        background: isDragging ? 'rgba(59, 130, 246, 0.05)' : 'transparent',
        opacity: isUploading ? 0.6 : 1
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes}
        onChange={handleFileInput}
        style={{ display: 'none' }}
        disabled={isUploading}
      />
      
      {isUploading ? (
        <>
          <RefreshCw size={48} style={{ color: 'var(--accent-primary)', marginBottom: 'var(--space-md)' }} className="animate-spin" />
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--space-xs)' }}>
            Processing document...
          </h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
            Extracting data from PDF
          </p>
        </>
      ) : (
        <>
          <Upload size={48} style={{ color: 'var(--accent-primary)', marginBottom: 'var(--space-md)' }} />
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--space-xs)' }}>
            Drop supplier quote PDF here
          </h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: 'var(--space-md)' }}>
            or click to browse files
          </p>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-dimmed)' }}>
            PDF files only • Max 10MB
          </p>
        </>
      )}
    </div>
  );
}

// ============ EXTRACTION SUMMARY CARD ============

export function ExtractionSummaryCard({ extraction, onReview }) {
  if (!extraction) return null;

  return (
    <div 
      data-testid="extraction-summary-card"
      style={{
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-lg)',
        marginBottom: 'var(--space-md)'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-md)' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
            <FileText size={18} />
            {extraction.supplier_name || 'Unknown Supplier'}
          </h3>
          {extraction.quote_reference && (
            <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Ref: {extraction.quote_reference}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
          <ConfidenceBadge confidence={extraction.extraction_confidence} />
          <StatusBadge status={extraction.status} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
        <div>
          <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Total</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>
            £{(extraction.total || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Line Items</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{extraction.line_items_count || 0}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Currency</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{extraction.currency || 'GBP'}</div>
        </div>
      </div>

      {extraction.fields_needing_review?.length > 0 && (
        <div style={{
          background: 'rgba(234, 179, 8, 0.1)',
          border: '1px solid rgba(234, 179, 8, 0.3)',
          borderRadius: 'var(--radius-sm)',
          padding: 'var(--space-sm) var(--space-md)',
          marginBottom: 'var(--space-md)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)'
        }}>
          <AlertTriangle size={14} style={{ color: 'var(--status-warning)' }} />
          <span style={{ fontSize: '0.75rem', color: 'var(--status-warning)' }}>
            Review needed: {extraction.fields_needing_review.join(', ')}
          </span>
        </div>
      )}

      <button
        onClick={onReview}
        className="btn btn-primary"
        data-testid="review-extraction-btn"
        style={{ width: '100%' }}
      >
        <Eye size={16} />
        Review & Verify Extraction
      </button>
    </div>
  );
}

// ============ LINE ITEM ROW (Editable) ============

export function LineItemRow({ item, index, onEdit, isEditing, onSave, onCancel }) {
  const [editedItem, setEditedItem] = useState(item);

  const handleFieldChange = (field, value) => {
    setEditedItem({ ...editedItem, [field]: value });
  };

  if (isEditing) {
    return (
      <tr data-testid={`line-item-row-${index}-editing`}>
        <td style={{ padding: 'var(--space-sm)' }}>{index + 1}</td>
        <td style={{ padding: 'var(--space-sm)' }}>
          <input
            type="text"
            value={editedItem.description || ''}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            style={{
              width: '100%',
              padding: '4px 8px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text-primary)',
              fontSize: '0.8125rem'
            }}
          />
        </td>
        <td style={{ padding: 'var(--space-sm)' }}>
          <input
            type="number"
            value={editedItem.quantity || ''}
            onChange={(e) => handleFieldChange('quantity', parseFloat(e.target.value) || null)}
            style={{
              width: '60px',
              padding: '4px 8px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text-primary)',
              fontSize: '0.8125rem'
            }}
          />
        </td>
        <td style={{ padding: 'var(--space-sm)' }}>
          <input
            type="number"
            value={editedItem.unit_price || ''}
            onChange={(e) => handleFieldChange('unit_price', parseFloat(e.target.value) || null)}
            style={{
              width: '80px',
              padding: '4px 8px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text-primary)',
              fontSize: '0.8125rem'
            }}
          />
        </td>
        <td style={{ padding: 'var(--space-sm)' }}>
          <input
            type="number"
            value={editedItem.line_total || ''}
            onChange={(e) => handleFieldChange('line_total', parseFloat(e.target.value) || null)}
            style={{
              width: '80px',
              padding: '4px 8px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text-primary)',
              fontSize: '0.8125rem'
            }}
          />
        </td>
        <td style={{ padding: 'var(--space-sm)' }}>
          <select
            value={editedItem.category || 'unknown'}
            onChange={(e) => handleFieldChange('category', e.target.value)}
            style={{
              padding: '4px 8px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text-primary)',
              fontSize: '0.75rem'
            }}
          >
            <option value="unknown">Unknown</option>
            <option value="units">Units</option>
            <option value="worktops">Worktops</option>
            <option value="appliances">Appliances</option>
            <option value="sinks_taps">Sinks & Taps</option>
            <option value="handles">Handles</option>
            <option value="delivery">Delivery</option>
            <option value="labour">Labour</option>
            <option value="accessories">Accessories</option>
            <option value="lighting">Lighting</option>
            <option value="other">Other</option>
          </select>
        </td>
        <td style={{ padding: 'var(--space-sm)' }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button 
              onClick={() => onSave(editedItem)} 
              style={{ 
                padding: '4px', 
                background: 'var(--status-success)', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: 'pointer',
                color: 'white'
              }}
            >
              <Save size={14} />
            </button>
            <button 
              onClick={onCancel}
              style={{ 
                padding: '4px', 
                background: 'var(--status-error)', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: 'pointer',
                color: 'white'
              }}
            >
              <X size={14} />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr 
      data-testid={`line-item-row-${index}`}
      style={{ 
        background: item.has_price_material_mismatch ? 'rgba(234, 179, 8, 0.05)' : 'transparent'
      }}
    >
      <td style={{ padding: 'var(--space-sm)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>{index + 1}</td>
      <td style={{ padding: 'var(--space-sm)' }}>
        <div style={{ fontSize: '0.8125rem' }}>{item.description}</div>
        {item.material_specification && (
          <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '2px' }}>
            Material: {item.material_specification}
          </div>
        )}
        {item.has_price_material_mismatch && (
          <div style={{ 
            fontSize: '0.625rem', 
            color: 'var(--status-warning)', 
            marginTop: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <AlertTriangle size={10} />
            {item.mismatch_note}
          </div>
        )}
      </td>
      <td style={{ padding: 'var(--space-sm)', fontSize: '0.8125rem', textAlign: 'right' }}>
        {item.quantity || '—'}
        {item.quantity_meta?.confidence && (
          <div style={{ marginTop: '2px' }}>
            <ConfidenceBadge confidence={item.quantity_meta.confidence} showLabel={false} />
          </div>
        )}
      </td>
      <td style={{ padding: 'var(--space-sm)', fontSize: '0.8125rem', textAlign: 'right' }}>
        {item.unit_price ? `£${item.unit_price.toFixed(2)}` : '—'}
      </td>
      <td style={{ padding: 'var(--space-sm)', fontSize: '0.8125rem', textAlign: 'right', fontWeight: 500 }}>
        {item.line_total ? `£${item.line_total.toFixed(2)}` : '—'}
        {item.line_total_meta?.confidence && (
          <div style={{ marginTop: '2px' }}>
            <ConfidenceBadge confidence={item.line_total_meta.confidence} showLabel={false} />
          </div>
        )}
      </td>
      <td style={{ padding: 'var(--space-sm)' }}>
        <span style={{
          display: 'inline-block',
          padding: '2px 6px',
          borderRadius: '4px',
          background: 'var(--bg-secondary)',
          fontSize: '0.6875rem',
          textTransform: 'capitalize'
        }}>
          {item.category?.replace(/_/g, ' ') || 'Unknown'}
        </span>
      </td>
      <td style={{ padding: 'var(--space-sm)' }}>
        <button
          onClick={() => onEdit(index)}
          style={{
            padding: '4px',
            background: 'transparent',
            border: '1px solid var(--glass-border)',
            borderRadius: '4px',
            cursor: 'pointer',
            color: 'var(--text-muted)'
          }}
        >
          <Edit2 size={14} />
        </button>
      </td>
    </tr>
  );
}

// ============ EXTRACTION REVIEW SCREEN ============

export function ExtractionReviewScreen({ quoteId, orgId, onVerified, onBack }) {
  const { token } = useAuth();
  const [quote, setQuote] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [corrections, setCorrections] = useState({});

  // Fetch quote details
  React.useEffect(() => {
    const fetchQuote = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/supplier-quotes/${quoteId}`,
          {
            params: { org_id: orgId },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setQuote(response.data.quote);
        setLineItems(response.data.line_items || []);
      } catch (err) {
        console.error('Failed to fetch quote:', err);
      } finally {
        setLoading(false);
      }
    };

    if (token && quoteId && orgId) {
      fetchQuote();
    }
  }, [token, quoteId, orgId]);

  const handleEditItem = (index) => {
    setEditingIndex(index);
  };

  const handleSaveItem = (editedItem) => {
    const newItems = [...lineItems];
    newItems[editingIndex] = editedItem;
    setLineItems(newItems);
    setEditingIndex(null);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  const handleVerify = async () => {
    setIsVerifying(true);
    try {
      await axios.post(
        `${API_URL}/api/supplier-quotes/${quoteId}/verify`,
        {
          ...corrections,
          corrections_note: corrections.note
        },
        {
          params: { org_id: orgId },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      onVerified && onVerified();
    } catch (err) {
      console.error('Failed to verify quote:', err);
    } finally {
      setIsVerifying(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="extraction-review-loading" style={{ textAlign: 'center', padding: 'var(--space-3xl)' }}>
        <RefreshCw size={24} className="animate-spin" style={{ color: 'var(--accent-primary)' }} />
        <div style={{ marginTop: 'var(--space-md)', color: 'var(--text-muted)' }}>Loading extraction...</div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div data-testid="extraction-review-error" style={{ textAlign: 'center', padding: 'var(--space-3xl)' }}>
        <AlertTriangle size={24} style={{ color: 'var(--status-error)' }} />
        <div style={{ marginTop: 'var(--space-md)', color: 'var(--text-muted)' }}>Quote not found</div>
      </div>
    );
  }

  return (
    <div data-testid="extraction-review-screen">
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: 'var(--space-lg)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
          <button 
            onClick={onBack}
            className="btn btn-secondary"
            style={{ padding: 'var(--space-sm)' }}
          >
            <ChevronLeft size={20} />
          </button>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Review Extraction</h2>
            <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Verify extracted data before creating quote
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
          <ConfidenceBadge confidence={quote.extraction_confidence} />
          <StatusBadge status={quote.status} />
        </div>
      </div>

      {/* Non-Negotiable Rules Notice */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-md)',
        marginBottom: 'var(--space-lg)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--space-sm)'
      }}>
        <Info size={18} style={{ color: 'rgb(59, 130, 246)', flexShrink: 0, marginTop: '2px' }} />
        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
          <strong>Human Verification Required:</strong> Review each extracted value against the source document. 
          Edit any incorrect values before confirming. Nothing enters the system without your approval.
        </div>
      </div>

      {/* Split View Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-lg)', marginBottom: 'var(--space-lg)' }}>
        
        {/* Left: Document Header */}
        <div style={{
          background: 'var(--glass-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-lg)'
        }}>
          <h3 style={{ margin: '0 0 var(--space-md)', fontSize: '0.875rem' }}>
            <FileText size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Document Info
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
            <div>
              <label style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Supplier</label>
              <div style={{ fontWeight: 500, marginTop: '4px' }}>{quote.supplier_name}</div>
              {quote.supplier_name_meta && <ConfidenceBadge confidence={quote.supplier_name_meta.confidence} showLabel={false} />}
            </div>
            <div>
              <label style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Reference</label>
              <div style={{ fontWeight: 500, marginTop: '4px' }}>{quote.quote_reference || '—'}</div>
            </div>
            <div>
              <label style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Source File</label>
              <div style={{ fontSize: '0.8125rem', marginTop: '4px' }}>{quote.source_document_name}</div>
            </div>
            <div>
              <label style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Pages</label>
              <div style={{ marginTop: '4px' }}>{quote.source_document_pages}</div>
            </div>
          </div>
        </div>

        {/* Right: Totals */}
        <div style={{
          background: 'var(--glass-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-lg)'
        }}>
          <h3 style={{ margin: '0 0 var(--space-md)', fontSize: '0.875rem' }}>Extracted Totals</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
            <div>
              <label style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Subtotal</label>
              <div style={{ fontSize: '1.125rem', fontWeight: 500, marginTop: '4px' }}>
                £{(quote.subtotal || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div>
              <label style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>VAT</label>
              <div style={{ fontSize: '1.125rem', fontWeight: 500, marginTop: '4px' }}>
                £{(quote.vat_amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Total</label>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, marginTop: '4px', color: 'var(--accent-primary)' }}>
                £{(quote.total || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </div>
              {quote.total_meta && <ConfidenceBadge confidence={quote.total_meta.confidence} />}
            </div>
          </div>
        </div>
      </div>

      {/* Line Items Table */}
      <div style={{
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        marginBottom: 'var(--space-lg)'
      }}>
        <div style={{ 
          padding: 'var(--space-md) var(--space-lg)',
          borderBottom: '1px solid var(--glass-border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0, fontSize: '0.875rem' }}>
            Line Items ({lineItems.length})
          </h3>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Click edit to correct any values
          </span>
        </div>
        
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                <th style={{ padding: 'var(--space-sm)', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>#</th>
                <th style={{ padding: 'var(--space-sm)', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Description</th>
                <th style={{ padding: 'var(--space-sm)', textAlign: 'right', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Qty</th>
                <th style={{ padding: 'var(--space-sm)', textAlign: 'right', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Unit Price</th>
                <th style={{ padding: 'var(--space-sm)', textAlign: 'right', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Total</th>
                <th style={{ padding: 'var(--space-sm)', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Category</th>
                <th style={{ padding: 'var(--space-sm)', width: '60px' }}></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, index) => (
                <LineItemRow
                  key={item.id || index}
                  item={item}
                  index={index}
                  onEdit={handleEditItem}
                  isEditing={editingIndex === index}
                  onSave={handleSaveItem}
                  onCancel={handleCancelEdit}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Verification Actions */}
      <div style={{
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-lg)'
      }}>
        <h3 style={{ margin: '0 0 var(--space-md)', fontSize: '0.875rem' }}>Confirm & Create Quote</h3>
        
        <div style={{ 
          background: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-md)',
          marginBottom: 'var(--space-md)'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)' }}>
            <CheckCircle size={18} style={{ color: 'var(--status-success)', flexShrink: 0, marginTop: '2px' }} />
            <div style={{ fontSize: '0.8125rem' }}>
              <strong>By confirming:</strong>
              <ul style={{ margin: '8px 0 0', paddingLeft: 'var(--space-md)' }}>
                <li>The extracted data will become a verified supplier quote</li>
                <li>Line items will be available for quote → job lineage</li>
                <li>Data will feed into margin forecasting</li>
              </ul>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
          <button
            onClick={onBack}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleVerify}
            className="btn btn-primary"
            disabled={isVerifying}
            data-testid="confirm-verify-btn"
            style={{ flex: 1 }}
          >
            {isVerifying ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Verifying...
              </>
            ) : (
              <>
                <Check size={16} />
                Confirm & Create Quote
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default {
  ConfidenceBadge,
  StatusBadge,
  UploadDropzone,
  ExtractionSummaryCard,
  LineItemRow,
  ExtractionReviewScreen
};
